import { Control } from 'react-hook-form';
import React, { memo } from 'react';
import { useToggle } from 'react-use';
import '../Filters/Filters.scss';

import { RangeField } from '../Filters/FilterFields/RangeField';
import { DateRangeField } from '../Filters/FilterFields/DateRangeField';

import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { formatMessageHelper } from '../../../shared/helpers/internationalization';
import { filterFields } from '../Filters/FilterFields/dataExplorerFieldsConfig';
import { FilterControls } from '../Filters/FilterFields/FilterControls';
import { InputField } from '../Filters/FilterFields/InputField';
import { RegionGroup } from '../Filters/FilterFields/RegionGroup';
import { CategoryField } from '../Filters/FilterFields/CategoryField';
import { DataCategory } from '../../../shared/api/api';
import { getInputId } from '../Filters/FilterFields/getInputId';
import { CheckboxListField } from '../Filters/FilterFields/CheckboxListField';
import { themeFieldValueLabels, themeFieldValues } from '../Filters/auxiliaryFields';
import { useSelector } from 'react-redux';
import { getUserPermissionSet } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';

type Props = {
  control: Control;
  reset: () => void;
  onCategoryModalOpen: () => void;
  selectedCategory?: DataCategory;
  isExtraFilterChanged?: boolean;
} & InjectedIntlProps;

const DataMainFiltersWithoutIntl = memo(({ intl, control, reset, onCategoryModalOpen, selectedCategory, isExtraFilterChanged }: Props) => {
  const [filtersOpen, setFiltersOpen] = useToggle(false);
  const permissions = useSelector(getUserPermissionSet);
  const isThemesAvailable = permissions.has('CONSULTANT') || permissions.has('RESEARCHER');

  return (
    <div>
      <div className="data-explorer-filters">
        <div className="data-explorer-filters__row">
          <div className="data-explorer-filters__search">
            <label htmlFor={getInputId(filterFields.search.name)} className="range-field__title">
              {formatMessageHelper(intl, 'dataExplorer.filters.search')}
            </label>
            <InputField
              controllerProps={{ name: filterFields.search.name, control }}
              inputProps={{
                type: 'search',
                className: 'filters__searchbar form__input form__input--large',
                placeholder: formatMessageHelper(intl, 'dataExplorer.filters.searchPlaceholder'),
              }}
            ></InputField>
          </div>

          <div>
            <CategoryField control={control} selectedCategory={selectedCategory} onOpenModal={onCategoryModalOpen} />
          </div>

          {isThemesAvailable && (
            <CheckboxListField
              title={formatMessageHelper(intl, 'dataExplorer.filters.themeLabel')}
              control={control}
              name={'theme'}
              valuesList={themeFieldValues}
              labelsList={themeFieldValueLabels}
            />
          )}

          <RegionGroup control={control} />

          <RangeField
            controllerProps={{ name: filterFields.valueScore.name, control }}
            title={formatMessageHelper(intl, 'dataExplorer.filters.valueScoreLabel')}
            min={filterFields.valueScore.validations.min}
          />

          <RangeField
            controllerProps={{ name: filterFields.alphaCount.name, control }}
            size="medium"
            title={formatMessageHelper(intl, 'dataExplorer.filters.alphasLabel')}
            min={filterFields.alphaCount.validations.min}
          />

          <DateRangeField
            controllerProps={{ name: filterFields.dateUpdated.name, control }}
            title={formatMessageHelper(intl, 'dataExplorer.filters.lastFieldAddedLabel')}
            placeholder={formatMessageHelper(intl, 'dataExplorer.filters.selectDatesPlaceholder')}
          />

          <FilterControls
            isExtraFieldsChanged={isExtraFilterChanged}
            reset={reset}
            filtersOpen={filtersOpen}
            setFiltersOpen={setFiltersOpen}
          />
        </div>
      </div>
    </div>
  );
});

export const DatasetFilters = injectIntl(DataMainFiltersWithoutIntl);
